import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { ReportsService } from '../../reports.service';
import { ReportsState } from 'src/app/shared/states/reports/reports.state';
import { Observable } from 'rxjs';
import { GetDrugsForDrugsFilter } from 'src/app/shared/states/drugs/drugs.actions';
import { GetUserTeamList, GetWholesalersList } from 'src/app/shared/states/profile/profile.actions';
import { UserStatusEnum } from 'src/app/shared/interfaces/UserStatusEnum';
import { ProfileState } from 'src/app/shared/states/profile/profile.state';
import { ITeamRecord } from 'src/app/team/interfaces/team-record.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DrugsState } from 'src/app/shared/states/drugs/drugs.state';
import { IDrug } from 'src/app/shared/interfaces/drug.interface';
import { groupByProperty, sortByProperty } from 'src/app/shared/helpers/helpers';
import { RunNdcPurchaseReport, SaveNdcPurchaseReportControlsState, SetLoading, SetNdcPurchaseRecords } from 'src/app/shared/states/reports/reports.actions';
import { INdcPurchaseRecord } from 'src/app/shared/interfaces/INdcPurchaseRecord.interface';
import { BaseTableDirective } from 'src/app/shared/directives/base-table.directive';
import { take } from 'rxjs/operators';
import { ITransaction } from 'src/app/shared/interfaces/transaction.interface';
import { Router } from '@angular/router';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { groupByGPI } from '../../helpers';
import { IWholesaler } from 'src/app/shared/interfaces/wholesaler.interface';

@UntilDestroy()
@Component({
  selector: 'app-ndc-purchase-report',
  templateUrl: './ndc-purchase-report.component.html',
  styleUrls: ['./ndc-purchase-report.component.scss']
})
export class NdcPurchaseReportComponent extends BaseTableDirective implements OnInit {

  controlsForm: UntypedFormGroup;

  isUsersDropdownOpened: boolean;
  isDrugsDropdownOpened: boolean;
  isWholesalersDropdownOpened: boolean;

  userItemsShowLimit = 2;
  drugItemsShowLimit = 2;
  wholesalerItemsShowLimit = 2;

  userListSettings = {
    selectAllText: 'All Users',
    allowSearchFilter: true,
    searchPlaceholderText: 'Search Users',
    noDataAvailablePlaceholderText: 'No users available',
  };

  drugListSettings = {
    selectAllText: 'All Drugs',
    allowSearchFilter: true,
    searchPlaceholderText: 'Search Drugs',
    noDataAvailablePlaceholderText: 'No drugs available',
  };

  wholesalerListSettings = {
    selectAllText: 'All Wholesalers',
    allowSearchFilter: true,
    searchPlaceholderText: 'Search Wholesalers',
    noDataAvailablePlaceholderText: 'No wholesalers available',
  };

  usersList = [];
  drugsList = [];
  originalRecordsList: INdcPurchaseRecord[] = [];
  sortedRecordsList: INdcPurchaseRecord[] = [];
  groupItemsOpened: boolean[] = [];

  isFileDownloading: boolean;
  title: string = "NDC Purchase Report"

  @ViewChild('periodDropdown') periodDropdown;
  @ViewChild('customPeriodDatepicker') customPeriodDatepicker;
  @ViewChild('usersMultioptionalDropdown') usersMultioptionalDropdown;
  @ViewChild('drugsMultioptionalDropdown') drugsMultioptionalDropdown;
  @ViewChild('wholesalersMultioptionalDropdown') wholesalersMultioptionalDropdown;
  @ViewChild('downloadMenu') downloadMenu;
  @ViewChild('tbody') tbody;

  @Select(ReportsState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(ProfileState.teamList) teamList$: Observable<ITeamRecord[]>;
  @Select(DrugsState.drugsFilterDrugItems) drugsFilterDrugItems$: Observable<IDrug[]>;
  @Select(ProfileState.wholesalers) wholesalers$: Observable<IWholesaler[]>;
  @Select(ReportsState.runningReports) runningReports$: Observable<boolean>;
  @Select(ReportsState.ndcPurchaseRecords) ndcPurchaseRecords$: Observable<INdcPurchaseRecord[]>;
  @Select(ReportsState.ndcPurchaseReportControlsState) ndcPurchaseReportControlsState$: Observable<any>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;

  isReportRun: boolean;

  tableColumns = {
    transactionDate: false,
    packageNdc: true,
    wholesalerSupplier: true,
    csos: true
  };

  filterResultsBy: 'drug' | 'wholesalerSupplier' | 'gpi' = 'drug';

  constructor(
    protected store: Store,
    private fb: UntypedFormBuilder,
    private reportsService: ReportsService,
    private router: Router,
  ) {
    super(store);

    this.controlsForm = this.fb.group({
      period: ['', []],
      customPeriod: [null, []],
      users: [[], []],
      drugs: [[], []],
      wholesalers: [[], []],
      scrollTop: 0,
      inventoryLocations: null
    });
  }

  ngOnInit(): void {
    this.store.dispatch(new GetDrugsForDrugsFilter());
    this.store.dispatch(new GetUserTeamList(UserStatusEnum.Active.toString()));

    this.teamList$.pipe(untilDestroyed(this))
      .subscribe((data: ITeamRecord[]) => {
        this.usersList = data?.map(el => ({id: el.id, name: el.userName})) || [];
      });

    this.drugsFilterDrugItems$.pipe(untilDestroyed(this))
    .subscribe((data: IDrug[]) => {
      this.populateDrugFilter(data);
    });

    this.store.dispatch(new GetWholesalersList(true));

    this.ndcPurchaseRecords$
    .pipe(untilDestroyed(this))
    .subscribe((records: INdcPurchaseRecord[]) => {
      if (records && records.length) {
        this.originalRecordsList = records;
        this.updateSortedList(this.originalRecordsList);
      } else {
        this.originalRecordsList = [];
      }
    })

    this.ndcPurchaseReportControlsState$.pipe(take(1)).subscribe((data) => {
      setTimeout(() => {
        this.controlsForm.patchValue(data);
        this.tbody?.nativeElement?.scrollTo(0, data?.scrollTop);
      });
      if (data?.scrollTop > 0) {
        setTimeout(() => {
          this.saveScrollPosition(0);
        }, 100);
      }
    });
  }

  runReport() {
    const raw = this.controlsForm.getRawValue();
    this.store.dispatch(new SaveNdcPurchaseReportControlsState(raw));
    this.store.dispatch(new RunNdcPurchaseReport({
      startDate: raw?.customPeriod?.[0] ? new Date(raw.customPeriod[0]).toDateString() : null,
      endDate: raw?.customPeriod?.[1] ? new Date(raw.customPeriod[1]).toDateString() : null,
      userIds: raw?.users?.map(x => x.id),
      drugIds: raw?.drugs?.map(x => x.id) || [],
      wholesalerIds: raw?.wholesalers?.map(x => x.id),
      searchDateRange: !raw?.period ? 0 : raw.period,
      inventoryLocations: raw?.inventoryLocations?.map(x => x.id)
    }));

    this.isReportRun = true;
  }


  focusMultioptionalDropdown(dropdownName: string): void {
    setTimeout(() => {
      this[dropdownName].opened = true;
      this[dropdownName].isFocused = true;
      this[dropdownName].dropDownOpened.emit();
      this[dropdownName].multiselectDropdown.nativeElement.focus();
    });
  }

  multiselectOpened(limitName: string, dropdownShowName: string): void {
    setTimeout(() => {
      this.userItemsShowLimit = 1;
      this.drugItemsShowLimit = 1;
      this.wholesalerItemsShowLimit = 1;

      this.isDrugsDropdownOpened = false;
      this.isUsersDropdownOpened = false;
      this.isWholesalersDropdownOpened = false;

      this[limitName] = 4;
      this[dropdownShowName] = true;
    });
  }

  multiselectClosed(limitName: string, dropdownShowName: string): void {
    this.userItemsShowLimit = 2;
    this.drugItemsShowLimit = 2;
    this.wholesalerItemsShowLimit = 2;
    this[dropdownShowName] = false;
  }

  private populateDrugFilter(drugs: IDrug[]): void {
    this.drugsList = drugs?.map(el => ({id: el.id, name: el.friendlyName, itemName: el.friendlyName, code: el.packageNdc}));
    this.drugsList = sortByProperty(this.drugsList, 'name', true);
  }

  areFiltersEmpty(): boolean {
    const formValue = this.controlsForm.getRawValue();
    return Object.values(formValue).every((x: any) => !x || (Array.isArray(x) && !x.length));
  }

  switchFilterResultBy(filterBy: 'drug' | 'wholesalerSupplier' | 'gpi'): void {
    this.filterResultsBy = filterBy;
    this.updateSortedList(this.originalRecordsList);
  }


  updateSortedList(source: INdcPurchaseRecord[]): void {
    const flatList = [...source].map((item: INdcPurchaseRecord) => {
      const record = {...item} as any;
      switch (this.filterResultsBy) {
        case 'drug':
          record.drug = `${record['friendlyName']} (${record['packageNdc']})`;
          break;
        case 'wholesalerSupplier':
          record.wholesalerSupplier = `${record['wholesalerSupplier']}`;
          break;
        case 'gpi':
          record.gpi = record['gpi'];
          break; 
      }

      return record;
    });
    setTimeout(() => {
      this.sortedRecordsList = this.filterResultsBy === 'gpi' ? groupByGPI(flatList, null, 'transactions') : groupByProperty(flatList, this.filterResultsBy);

      this.groupItemsOpened = Array(this.sortedRecordsList.length).fill(true);
      this.store.dispatch(new SetLoading(false));
    });
  }

  sortByColumn(columnName: string): void {
    this.store.dispatch(new SetNdcPurchaseRecords(this.sort(this.originalRecordsList, columnName)));
  }

  reset(): void {
    this.controlsForm.reset();
    this.store.dispatch(new SaveNdcPurchaseReportControlsState(null));
    this.originalRecordsList = [];
  }


  saveCSV(): void {
    this.store.dispatch(new SetLoading(true));
    this.isFileDownloading = true;
    this.reportsService.downloadNdcPurchaseCsvReport({models: this.getEntitiesToExport(), withLocations: this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)})
      .pipe(take(1))
      .subscribe(res => {
        const name = this.title + ' from ' + new Date().toDateString() + '.csv';
        this.reportsService.handleDownload(res, name);
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      }, err => {
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      });
  }
  
  savePDF(): void {
    this.store.dispatch(new SetLoading(true));
    this.isFileDownloading = true;
    const raw = this.controlsForm.getRawValue();
    this.reportsService.downloadNdcPurchasePdfReport({
      models: this.getEntitiesToExport(),
      startDate: raw?.customPeriod?.[0] ? new Date(raw.customPeriod[0]).toDateString() : null,
      endDate: raw?.customPeriod?.[1] ? new Date(raw.customPeriod[1]).toDateString() : null,
      searchDateRange: !raw?.period ? 0 : raw.period,
      byAllDrugs: raw.drugs?.length,
      withLocations: this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)
    })
    .pipe(take(1))
    .subscribe(res => {
      const name = this.title + ' from ' + new Date().toDateString() + '.pdf';
      this.reportsService.handleDownload(res, name);
      this.store.dispatch(new SetLoading(false));
      this.isFileDownloading = false;
    }, err => {
      this.store.dispatch(new SetLoading(false));
      this.isFileDownloading = false;
    });
  }

  getEntitiesToExport() {
    const groups = this.sortedRecordsList.map((x: any) => x?.transactions?.map(x => {
      const localTime = new Date(x.transactionDate).toString();
     return {...x, transactionDate: localTime.split("GMT")[0].trim()}
    }));
    const merged = [];
    groups.forEach(array => {
      merged.push(...array as any);
    });

    return merged;
  }


  trackByFn(index, item): number | string {
    return item.id;
  }

  openOrder(id: number): void {
    this.saveScrollPosition(this.tbody?.nativeElement?.scrollTop || 0);
    this.router.navigate(['inventory/order-history/details', id], {state: { navigatedFrom: this.router.url }});
  }

  onScroll(ev: Event): void {
    document.querySelector('table').scrollTop = (ev.target as any).scrollTop;
  }

  private saveScrollPosition(scrollTop: number) {
    this.controlsForm.get('scrollTop').setValue(scrollTop);
    this.store.dispatch(new SaveNdcPurchaseReportControlsState(this.controlsForm.getRawValue()));
  }

}
